import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Building2, FileText, CheckCircle2, XCircle, Clock, ExternalLink, RefreshCw, Zap, Loader2, Settings, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

function mapSuperpdpInvoiceError(message: string): string {
  if (message === 'Tenant city and postal code are required') {
    return 'La fonction superpdp-invoice en production est trop ancienne (elle lit tenant.city). Déployez-la. L’adresse de la SCI ne suffit pas : il faut ville/CP du locataire de la période testée.';
  }
  return message;
}

async function readEdgeFunctionError(error: unknown, data?: { error?: string } | null): Promise<string> {
  if (data?.error) return mapSuperpdpInvoiceError(data.error);

  const err = error as { message?: string; context?: Response };
  const response = err?.context;
  if (response && typeof response.text === 'function') {
    try {
      const text = await response.clone().text();
      if (text) {
        try {
          const json = JSON.parse(text);
          if (json?.error) {
            const raw = typeof json.error === 'string' ? json.error : JSON.stringify(json.error);
            return mapSuperpdpInvoiceError(raw);
          }
          if (json?.message) return mapSuperpdpInvoiceError(json.message);
        } catch {
          return mapSuperpdpInvoiceError(text);
        }
      }
    } catch {
      // fall through
    }
  }

  return mapSuperpdpInvoiceError(err?.message || 'Unknown error');
}

function tenantDisplayName(tenant: any): string {
  if (!tenant) return 'locataire';
  if (tenant.tenant_type === 'company' || tenant.tenant_type === 'professional') {
    return tenant.company_name || 'locataire';
  }
  return `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim() || 'locataire';
}

function buyerAddressFromLease(lease: any): { city: string; postal: string } {
  const tenant = lease?.tenants;
  const property = lease?.properties;
  return {
    city: (tenant?.ville_locataire || tenant?.ville_bien || property?.city || '').trim(),
    postal: (tenant?.code_postal_locataire || tenant?.code_postal_bien || property?.postal_code || '').trim(),
  };
}

export default function SuperPDPConfig() {
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [selectedTestSci, setSelectedTestSci] = useState<string>('');
  const [selectedTestPeriod, setSelectedTestPeriod] = useState<string>('');
  const [editingConfig, setEditingConfig] = useState<any>(null);
  const queryClient = useQueryClient();

  // Fetch SCIs with their SUPER PDP configurations
  const { data: scis = [], isLoading: scisLoading } = useQuery({
    queryKey: ['scis-superpdp-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select('id, name, siret, address, city, postal_code')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  // Fetch SUPER PDP configuration (organization-level, single config)
  const { data: config } = useQuery({
    queryKey: ['superpdp-configuration'],
    queryFn: async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) return null;

      const { data, error } = await supabase
        .from('superpdp_configurations')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch SUPER PDP invoices history
  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ['superpdp-invoices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('superpdp_invoices')
        .select(`
          *,
          scis (name),
          rent_periods (
            period_yyyymm,
            leases (
              properties (name, address),
              tenants (first_name, last_name, company_name, tenant_type)
            )
          )
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch rent periods for testing
  const { data: rentPeriods = [] } = useQuery({
    queryKey: ['rent-periods-for-test'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rent_periods')
        .select(`
          id, period_yyyymm, total_due,
          leases (
            properties (name, address, city, postal_code, sci_id, scis (id, name)),
            tenants (first_name, last_name, company_name, tenant_type, ville_locataire, code_postal_locataire, ville_bien, code_postal_bien)
          )
        `)
        .order('period_yyyymm', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data;
    },
  });

  // Test SUPER PDP integration mutation
  const testSuperPDPMutation = useMutation({
    mutationFn: async ({ sciId, rentPeriodId }: { sciId: string; rentPeriodId: string }) => {
      console.log('Testing SUPER PDP integration with:', { sciId, rentPeriodId });
      
      const { data, error } = await supabase.functions.invoke('superpdp-invoice', {
        body: {
          sciId,
          rentPeriodId,
          invoiceType: 'avis_echeance',
          sendInvoice: false, // Don't send in test mode
        },
      });

      if (error || data?.error) {
        throw new Error(await readEdgeFunctionError(error, data));
      }

      return data;
    },
    onSuccess: (data) => {
      toast.success(`Facture créée avec succès: ${data.invoiceNumber}`);
      queryClient.invalidateQueries({ queryKey: ['superpdp-invoices'] });
    },
    onError: (error: any) => {
      console.error('SUPER PDP test error:', error);
      toast.error(`Erreur SUPER PDP: ${error.message}`);
    },
  });

  // Delete invoice mutation
  const deleteInvoiceMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from('superpdp_invoices')
        .delete()
        .eq('id', invoiceId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Facture supprimée avec succès');
      queryClient.invalidateQueries({ queryKey: ['superpdp-invoices'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });

  // Save configuration mutation (organization-level)
  const saveConfigMutation = useMutation({
    mutationFn: async (configData: {
      clientId: string;
      clientSecret: string;
      isSandbox: boolean;
      isActive: boolean;
    }) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('Organization not found');

      if (config) {
        // Update existing config
        const { error } = await supabase
          .from('superpdp_configurations')
          .update({
            superpdp_client_id: configData.clientId,
            superpdp_client_secret: configData.clientSecret,
            is_sandbox: configData.isSandbox,
            is_active: configData.isActive,
            updated_at: new Date().toISOString(),
          })
          .eq('id', config.id);
        if (error) throw error;
      } else {
        // Create new config
        const { error } = await supabase
          .from('superpdp_configurations')
          .insert({
            organization_id: profile.organization_id,
            superpdp_client_id: configData.clientId,
            superpdp_client_secret: configData.clientSecret,
            is_sandbox: configData.isSandbox,
            is_active: configData.isActive,
          });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success('Configuration sauvegardée');
      queryClient.invalidateQueries({ queryKey: ['superpdp-configuration'] });
      setEditingConfig(null);
    },
    onError: (error: any) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'sent':
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle2 className="h-3 w-3 mr-1" /> Envoyé</Badge>;
      case 'created':
        return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20"><CheckCircle2 className="h-3 w-3 mr-1" /> Créé</Badge>;
      case 'validated':
        return <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/20"><CheckCircle2 className="h-3 w-3 mr-1" /> Validé</Badge>;
      case 'error':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" /> Erreur</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" /> En attente</Badge>;
    }
  };

  const getInvoiceTypeBadge = (type: string) => {
    if (type === 'quittance') {
      return <Badge className="bg-primary/10 text-primary border-primary/20">Quittance</Badge>;
    }
    return <Badge variant="outline">Avis d'échéance</Badge>;
  };

  const invoiceColumns = [
    { 
      header: 'Date', 
      accessorKey: (row: any) => format(new Date(row.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })
    },
    { header: 'N° Facture', accessorKey: 'invoice_number' as const },
    { header: 'Type', accessorKey: (row: any) => getInvoiceTypeBadge(row.invoice_type) },
    { header: 'SCI', accessorKey: (row: any) => row.scis?.name || '-' },
    {
      header: 'Locataire',
      accessorKey: (row: any) => {
        const tenant = row.rent_periods?.leases?.tenants;
        if (!tenant) return '-';
        if (tenant.tenant_type === 'company') return tenant.company_name;
        return `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim();
      },
    },
    { 
      header: 'Montant TTC', 
      accessorKey: (row: any) => `${Number(row.amount_ttc).toFixed(2)} €` 
    },
    { header: 'Statut', accessorKey: (row: any) => getStatusBadge(row.status) },
    {
      header: 'Actions',
      accessorKey: (row: any) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedInvoice(row)}
            title="Voir les détails"
          >
            <FileText className="h-4 w-4" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-destructive hover:text-destructive"
                title="Supprimer la facture"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer la facture</AlertDialogTitle>
                <AlertDialogDescription>
                  Êtes-vous sûr de vouloir supprimer la facture {row.invoice_number} ? Cette action est irréversible.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteInvoiceMutation.mutate(row.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Supprimer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ),
    },
  ];

  // Statistics
  const stats = {
    total: invoices.length,
    sent: invoices.filter(i => i.status === 'sent').length,
    created: invoices.filter(i => i.status === 'created').length,
    validated: invoices.filter(i => i.status === 'validated').length,
    errors: invoices.filter(i => i.status === 'error').length,
    totalAmount: invoices.reduce((sum, i) => sum + Number(i.amount_ttc || 0), 0),
  };

  return (
    <AppLayout title="SUPER PDP" description="Gérez l'intégration avec SUPER PDP pour la facturation électronique">
      <PageHeader
        title="SUPER PDP"
        description="Intégration facturation électronique (Peppol BIS)"
        actions={
          <Button
            variant="outline"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['superpdp-invoices'] })}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        }
      />

      <Tabs defaultValue="scis" className="space-y-6">
        <TabsList>
          <TabsTrigger value="scis">Configuration par SCI</TabsTrigger>
          <TabsTrigger value="history">Historique des factures ({stats.total})</TabsTrigger>
        </TabsList>

        <TabsContent value="scis" className="space-y-6">
          {/* Test Integration Card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">Tester l'intégration</CardTitle>
              </div>
              <CardDescription>
                Sélectionnez une SCI et une période pour créer une facture test dans SUPER PDP
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">SCI</label>
                  <Select value={selectedTestSci} onValueChange={setSelectedTestSci}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une SCI" />
                    </SelectTrigger>
                    <SelectContent>
                      {scis.map((sci) => (
                        <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Période de loyer</label>
                  <Select value={selectedTestPeriod} onValueChange={setSelectedTestPeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une période" />
                    </SelectTrigger>
                    <SelectContent>
                      {rentPeriods
                        .filter((rp: any) => !selectedTestSci || rp.leases?.properties?.sci_id === selectedTestSci)
                        .map((rp: any) => {
                          const tenant = rp.leases?.tenants;
                          const tenantName = tenant?.tenant_type === 'company' 
                            ? tenant.company_name 
                            : `${tenant?.first_name || ''} ${tenant?.last_name || ''}`.trim();
                          return (
                            <SelectItem key={rp.id} value={rp.id}>
                              {rp.period_yyyymm} - {tenantName} ({rp.total_due}€)
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => {
                      if (!selectedTestSci || !selectedTestPeriod) {
                        toast.error('Sélectionnez une SCI et une période');
                        return;
                      }
                      const period = rentPeriods.find((rp: any) => rp.id === selectedTestPeriod);
                      const { city, postal } = buyerAddressFromLease(period?.leases);
                      if (!city || !postal) {
                        toast.error(`Le locataire «${tenantDisplayName(period?.leases?.tenants)}» n'a pas de ville/code postal. Renseignez l'adresse du locataire (page Locataires), pas celle de la SCI.`);
                        return;
                      }
                      testSuperPDPMutation.mutate({ 
                        sciId: selectedTestSci, 
                        rentPeriodId: selectedTestPeriod 
                      });
                    }}
                    disabled={!selectedTestSci || !selectedTestPeriod || testSuperPDPMutation.isPending}
                    className="w-full"
                  >
                    {testSuperPDPMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Tester l'intégration
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total factures</CardDescription>
                <CardTitle className="text-3xl">{stats.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Envoyées</CardDescription>
                <CardTitle className="text-3xl text-green-600">{stats.sent}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Erreurs</CardDescription>
                <CardTitle className="text-3xl text-destructive">{stats.errors}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Montant total</CardDescription>
                <CardTitle className="text-3xl">{stats.totalAmount.toFixed(2)} €</CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Organization-level Configuration */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Configuration SUPER PDP</CardTitle>
                  <CardDescription>
                    Une seule configuration pour toutes vos SCI. Les identifiants seront utilisés pour toutes les factures.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setEditingConfig({ config: config || { superpdp_client_id: '', superpdp_client_secret: '', is_sandbox: true, is_active: false } } as any)}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  {config ? 'Modifier' : 'Configurer'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {config ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Statut</span>
                    <Badge variant={config.is_active ? 'default' : 'secondary'}>
                      {config.is_active ? 'Actif' : 'Inactif'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Environnement</span>
                    <Badge variant={config.is_sandbox ? 'outline' : 'default'}>
                      {config.is_sandbox ? 'Sandbox' : 'Production'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Client ID</span>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {config.superpdp_client_id ? '••••••••' : 'Non configuré'}
                    </code>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-4">
                    Aucune configuration SUPER PDP. Cliquez sur "Configurer" pour commencer.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SCI Statistics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {scis.map((sci) => {
              const sciInvoices = invoices.filter(i => i.sci_id === sci.id);

              return (
                <Card key={sci.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <Building2 className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{sci.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {sci.siret || 'SIRET non renseigné'}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      {sci.address && (
                        <p>{sci.address}, {sci.postal_code} {sci.city}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Factures envoyées</span>
                      <Badge variant="secondary">{sciInvoices.length}</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {scis.length === 0 && !scisLoading && (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">Aucune SCI configurée</p>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Historique des factures</CardTitle>
              <CardDescription>
                Toutes les factures envoyées via l'intégration SUPER PDP
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={invoiceColumns}
                data={invoices}
                isLoading={invoicesLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Configuration Dialog */}
      {editingConfig && (
        <Sheet open={!!editingConfig} onOpenChange={() => setEditingConfig(null)}>
          <SheetContent className="w-[500px]">
            <SheetHeader>
              <SheetTitle>Configuration SUPER PDP</SheetTitle>
            </SheetHeader>
            <ScrollArea className="h-[calc(100vh-120px)] pr-4">
              <div className="space-y-4 py-6">
                <div className="space-y-2">
                  <Label>Client ID *</Label>
                  <Input
                    value={(editingConfig.config as any)?.superpdp_client_id || ''}
                    onChange={(e) => setEditingConfig({
                      ...editingConfig,
                      config: { ...(editingConfig.config as any), superpdp_client_id: e.target.value }
                    } as any)}
                    placeholder="Votre Client ID SUPER PDP"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ce Client ID sera utilisé pour toutes vos SCI
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Client Secret *</Label>
                  <Input
                    type="password"
                    value={(editingConfig.config as any)?.superpdp_client_secret || ''}
                    onChange={(e) => setEditingConfig({
                      ...editingConfig,
                      config: { ...(editingConfig.config as any), superpdp_client_secret: e.target.value }
                    } as any)}
                    placeholder="Votre Client Secret SUPER PDP"
                  />
                  <p className="text-xs text-muted-foreground">
                    Ce Client Secret sera utilisé pour toutes vos SCI
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={(editingConfig.config as any)?.is_sandbox ?? true}
                    onCheckedChange={(checked) => setEditingConfig({
                      ...editingConfig,
                      config: { ...(editingConfig.config as any), is_sandbox: checked }
                    } as any)}
                  />
                  <Label>Utiliser l'environnement Sandbox</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={(editingConfig.config as any)?.is_active ?? false}
                    onCheckedChange={(checked) => setEditingConfig({
                      ...editingConfig,
                      config: { ...(editingConfig.config as any), is_active: checked }
                    } as any)}
                  />
                  <Label>Activer l'intégration</Label>
                </div>
                <Button
                  className="w-full"
                  onClick={() => {
                    const cfg = editingConfig.config as any;
                    if (!cfg?.superpdp_client_id || !cfg?.superpdp_client_secret) {
                      toast.error('Veuillez remplir tous les champs requis');
                      return;
                    }
                    saveConfigMutation.mutate({
                      clientId: cfg.superpdp_client_id,
                      clientSecret: cfg.superpdp_client_secret,
                      isSandbox: cfg.is_sandbox ?? true,
                      isActive: cfg.is_active ?? false,
                    });
                  }}
                  disabled={saveConfigMutation.isPending}
                >
                  {saveConfigMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </Button>
              </div>
            </ScrollArea>
          </SheetContent>
        </Sheet>
      )}

      {/* Invoice Detail Sheet */}
      <Sheet open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <SheetContent className="w-[500px]">
          <SheetHeader>
            <SheetTitle>Détails de la facture</SheetTitle>
          </SheetHeader>
          {selectedInvoice && (
            <ScrollArea className="h-[calc(100vh-120px)] pr-4">
              <div className="space-y-6 py-6">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Numéro de facture</h4>
                  <p className="font-mono text-lg">{selectedInvoice.invoice_number}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Type</h4>
                    {getInvoiceTypeBadge(selectedInvoice.invoice_type)}
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Statut</h4>
                    {getStatusBadge(selectedInvoice.status)}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">SCI</h4>
                  <p>{selectedInvoice.scis?.name}</p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Période</h4>
                  <p>{selectedInvoice.rent_periods?.period_yyyymm}</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Montant HT</h4>
                    <p className="font-semibold">{Number(selectedInvoice.amount_ht).toFixed(2)} €</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">TVA</h4>
                    <p className="font-semibold">{Number(selectedInvoice.vat_amount).toFixed(2)} €</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Montant TTC</h4>
                    <p className="font-semibold text-primary">{Number(selectedInvoice.amount_ttc).toFixed(2)} €</p>
                  </div>
                </div>

                {selectedInvoice.superpdp_invoice_id && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">ID SUPER PDP</h4>
                    <code className="text-sm bg-muted px-2 py-1 rounded block">
                      {selectedInvoice.superpdp_invoice_id}
                    </code>
                  </div>
                )}

                {selectedInvoice.buyer_email_address && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Adresse acheteur</h4>
                    <code className="text-sm bg-muted px-2 py-1 rounded block">
                      {selectedInvoice.buyer_email_address}
                    </code>
                  </div>
                )}

                {selectedInvoice.error_message && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-destructive">Message d'erreur</h4>
                    <p className="text-sm text-destructive bg-destructive/10 p-3 rounded">
                      {selectedInvoice.error_message}
                    </p>
                  </div>
                )}

                {selectedInvoice.superpdp_response && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Réponse SUPER PDP</h4>
                    <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-48">
                      {JSON.stringify(selectedInvoice.superpdp_response, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}

